import { TipoCatalogo } from "../../../../../lib/analytics";

/**
 * Lo único que distingue a /compra-tu-carro, /compra-tu-moto, /compra-tu-camion y
 * /compra-tu-van: una ruta, un `body_type`, unos textos.
 *
 * Antes eran cuatro copias del mismo archivo de ~950 líneas. Las tres copias se
 * quedaron atrás del original (sin multi-selección, sin sedes, sin tracción) y en
 * ellas vivía el `TypeError` que dejaba los tres catálogos en "NO HAY RESULTADOS".
 * Con una sola página parametrizada, lo que se arregla en una se arregla en todas.
 */
export interface FiltroFijo {
    /** Modelo (año) mínimo, inclusive. */
    anioMinimo?: number;
    /** Precio máximo en pesos, inclusive. */
    precioMaximo?: number;
    /** Ids de tipo de vehículo (`body_type`) que nunca se muestran, p. ej. "18" (motos). */
    tiposExcluidos?: string[];
}

type RangoPrecio = { min: number; max: number } | undefined;

/**
 * Cruza lo que eligió el usuario con el filtro fijo del catálogo.
 * - Año: el backend recibe los años separados por guion (así llega la multi-selección).
 *   Sin selección se envían todos desde `anioMinimo` hasta el modelo del año siguiente;
 *   con selección se descartan los anteriores al mínimo.
 * - Precio: el tope nunca supera `precioMaximo`.
 */
export const aplicarFiltroFijo = (
    filtro: FiltroFijo | undefined,
    year: string | undefined,
    price: RangoPrecio,
    anioActual: number = new Date().getFullYear(),
): { year: string | undefined; price: RangoPrecio } => {
    if (!filtro) return { year, price };

    let nextYear = year;
    if (filtro.anioMinimo !== undefined) {
        const minimo = filtro.anioMinimo;
        const elegidos = String(year ?? "")
            .split("-")
            .map(Number)
            .filter((n) => !isNaN(n) && n >= minimo);
        const permitidos =
            elegidos.length > 0
                ? elegidos
                : Array.from({ length: Math.max(anioActual + 1 - minimo + 1, 1) }, (_, i) => minimo + i);
        nextYear = permitidos.join("-");
    }

    let nextPrice = price;
    if (filtro.precioMaximo !== undefined) {
        const tope = filtro.precioMaximo;
        const maxElegido = price?.max;
        nextPrice = {
            min: price?.min as number,
            max: maxElegido !== undefined && maxElegido !== null && maxElegido < tope ? maxElegido : tope,
        };
    }

    return { year: nextYear, price: nextPrice };
};

/**
 * El backend solo filtra por tipos incluidos, así que "todo menos motos" se pide
 * como la lista de los demás tipos. `todosLosTipos` son los ids del contexto.
 * Con selección del usuario solo se descartan los excluidos.
 */
export const aplicarTiposExcluidos = (
    filtro: FiltroFijo | undefined,
    seleccionados: string[] | undefined,
    todosLosTipos: string[],
): string[] | undefined => {
    const excluidos = filtro?.tiposExcluidos;
    if (!excluidos?.length) return seleccionados;
    const permitidos = (seleccionados?.length ? seleccionados : todosLosTipos).filter(
        (id) => !excluidos.includes(String(id)),
    );
    return permitidos.length ? permitidos : todosLosTipos.filter((id) => !excluidos.includes(String(id)));
};

export const etiquetasFiltroFijo = (filtro: FiltroFijo | undefined): string[] => {
    if (!filtro) return [];
    const etiquetas: string[] = [];
    if (filtro.anioMinimo !== undefined) etiquetas.push(`Modelo ${filtro.anioMinimo} en adelante`);
    if (filtro.precioMaximo !== undefined) etiquetas.push(`Hasta $${filtro.precioMaximo / 1000000}M`);
    if (filtro.tiposExcluidos?.length) etiquetas.push("Sin motos");
    return etiquetas;
};

export interface CatalogoConfig {
    /** Discriminador de los eventos de analítica. */
    tipo: TipoCatalogo;
    /** Ruta base, para "Limpiar filtros". */
    ruta: string;
    /**
     * Acota el catálogo a un tipo del backend (`body_type`). Cuando está definido,
     * el filtro "Tipo" desaparece: el catálogo entero ya es ese tipo.
     * Sin él, el tipo lo elige el usuario, que es el caso de /compra-tu-carro.
     */
    bodyTypeFijo?: string;
    /**
     * Prefijo de la ruta del detalle cuando difiere de /compra-tu-carro (p. ej. el
     * catálogo inDrive abre /compra-tu-carro/indrive/:tipo/:nombre/:id).
     */
    rutaDetalle?: string;
    /**
     * Filtros que el usuario no puede quitar ni salirse de ellos. Se aplican al
     * buscar, no en el formulario, así "Limpiar filtros" tampoco los borra.
     */
    filtroFijo?: FiltroFijo;
    /** Encabezado del contador de resultados. */
    etiquetaConteo: string;
    helmet: {
        title: string;
        description: string;
        keywords: string;
        canonical: string;
    };
}

export const CATALOGO_CARROS: CatalogoConfig = {
    tipo: "carros",
    ruta: "/compra-tu-carro",
    etiquetaConteo: "Vehículos encontrados",
    helmet: {
        title: "Compra tu Carro | camionetas usadas - wcar",
        description: "Compra tu carro usado de forma fácil y segura con wcar",
        keywords: "Compra tu carro",
        canonical: "https://wcar.co/compra-o-vende-tu-carro-en-colombia",
    },
};

// Mismo inventario y mismos filtros que /compra-tu-carro; solo cambia la ruta y
// el banner de inDrive. Más adelante ambas vistas van a diferir.
export const CATALOGO_INDRIVE: CatalogoConfig = {
    tipo: "indrive",
    ruta: "/compra-tu-carro/indrive",
    rutaDetalle: "/compra-tu-carro/indrive",
    filtroFijo: { anioMinimo: 2020, precioMaximo: 70000000, tiposExcluidos: ["18"] },
    etiquetaConteo: "Vehículos encontrados",
    helmet: {
        title: "Compra tu Carro con inDrive | wcar",
        description: "Obtén bonos exclusivos y hasta el 100% de financiación con wcar e inDrive",
        keywords: "Compra tu carro, inDrive, wcar",
        canonical: "https://wcar.co/compra-tu-carro/indrive",
    },
};

export const CATALOGO_MOTOS: CatalogoConfig = {
    tipo: "motos",
    ruta: "/compra-tu-moto",
    bodyTypeFijo: "18",
    etiquetaConteo: "Motos encontradas",
    helmet: {
        title: "Compra motos usadas y de segunda a crédito en Colombia | wcar",
        description:
            "Compra tu moto usada con total transparencia. En wcar encuentras motocicletas de segunda, motos financiadas y opciones buenas, bonitas y baratas. Sin letra pequeña.",
        keywords:
            "motocicleta de segunda, motos usadas, motos de segunda mano, compra de motos usadas, concesionario de motos usadas, motos usadas a crédito, motos de segunda a crédito, motos de segunda financiadas, vender moto usada, motos deportivas económicas, motoneta usada barata, moto buena bonita y barata",
        canonical: "https://wcar.co/compra-tu-moto",
    },
};

// ⚠️ El backend no tiene un tipo 21: /api/type-cars devuelve 2, 3, 6, 8, 12, 13, 18
// y 22. Este catálogo responde 0 resultados siempre, y así estaba antes de unificar.
// Se conserva el id tal cual para no cambiar el comportamiento aquí; darle inventario
// es una decisión de backend/producto.
export const CATALOGO_CAMIONES: CatalogoConfig = {
    tipo: "camiones",
    ruta: "/compra-tu-camion",
    bodyTypeFijo: "21",
    etiquetaConteo: "Camiones encontrados",
    helmet: {
        title: "Compra tu Carro - wcar",
        description: "Compra tu carro usado de forma fácil y segura con wcar",
        keywords: "Compra tu carro",
        canonical: "https://wcar.co/compra-o-vende-tu-carro-en-colombia",
    },
};

export const CATALOGO_VANS: CatalogoConfig = {
    tipo: "vans",
    ruta: "/compra-tu-van",
    bodyTypeFijo: "22",
    etiquetaConteo: "Vans encontradas",
    helmet: {
        title: "Compra tu Carro - wcar",
        description: "Compra tu carro usado de forma fácil y segura con wcar",
        keywords: "Compra tu carro",
        canonical: "https://wcar.co/compra-o-vende-tu-carro-en-colombia",
    },
};
